/** @jsx jsx */

import { FormattedMessage } from 'react-intl';
import { Fragment } from 'react';

import { jsx } from '@emotion/core';
import { Colors, Grid, Icon, Text } from '@zapier/design-system';

import LinkWithArrow from 'app/developer-v3/components/LinkWithArrow';
import Link from 'app/common/components/Link';
import { getAppUrl } from 'app/developer-v3/utils';
import {
  TABS as SIDEBAR_RIGHT_TABS,
  VERSIONING_SECTION_IDS,
  useSidebarRight,
} from 'app/developer-v3/components/SidebarRight';
import { LifecycleChangeType } from 'app/entities/VersionLifecycle/types';

import Styles from './CTAVersioning.styles';

type Props = {
  error?: { messages: string[] };
  integrationId: ID;
  type: LifecycleChangeType;
};

function Error(props: Props) {
  const { error, integrationId, type } = props;

  const { update: updateSidebarRight } = useSidebarRight();

  const isSupportedType = [
    LifecycleChangeType.promote,
    LifecycleChangeType.migrate,
  ].includes(type);

  if (!isSupportedType) {
    return null;
  }
  const isPromotion = type === LifecycleChangeType.promote;

  const title = isPromotion
    ? 'Something went wrong while promoting your integration version.'
    : 'Something went wrong while migrating your integration version.';
  const activeSubsection = isPromotion
    ? VERSIONING_SECTION_IDS.PROMOTE
    : VERSIONING_SECTION_IDS.MIGRATE;

  return (
    <div css={Styles.root(Colors.StatusErrorWeakest)}>
      <Grid columns="30px auto 20px" gap={20}>
        <Icon color="StatusError" name="alertCircleFill" size={30} />
        <div>
          <Text fontWeight={600} tag="h2" type="Body4">
            {title}
          </Text>
          {error?.messages &&
            error.messages.map(message => (
              <Text key={message} margin="10px 0px 0px" tag="p">
                {message}
              </Text>
            ))}
          <Text margin="10px 0px 00px 0px" tag="p">
            <FormattedMessage
              defaultMessage="Please contact Zapier support if you continue to get errors."
              id="messages-contact-support-if-errors-persist"
            />
            {isPromotion ? (
              <Fragment>
                <FormattedMessage
                  defaultMessage=" You can try promote the version again from the <component0>versions</component0> page."
                  id="messages-retry-promotion-from-versions-page"
                  values={{
                    component0: chunks => (
                      <Link href={`${getAppUrl(integrationId)}/versions/`}>
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </Fragment>
            ) : (
              <Fragment>
                <FormattedMessage
                  defaultMessage=" You can try again using the"
                  id="messages-retry-using"
                />{' '}
                <LinkWithArrow
                  onClick={() =>
                    updateSidebarRight({
                      tabActive: SIDEBAR_RIGHT_TABS.VERSIONING,
                      activeSubsection,
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="side panel"
                    id="links-side-panel"
                  />
                </LinkWithArrow>
                .
              </Fragment>
            )}
          </Text>
        </div>
      </Grid>
    </div>
  );
}

export default Error;
